/**
 * What a refusal means, in words and in shape.
 *
 * The engine names every refusal — `fail('needs_a_plate')`, `fail('pan_burnt')`
 * — and the name travels all the way to the renderer on `Cue.reason`. Until
 * this table existed the renderer threw it away and drew the same red cross for
 * every one of the thirty-seven, so the player was told "no" and never told
 * "why". That is the single most reported source of confusion in the game.
 *
 * Two channels come out of one table, deliberately:
 *
 *   - the **phrase** is a plain sentence for the flash line, addressed to the
 *     player and naming the fix where there is one ("wash one at the sink"),
 *     never the engine's identifier;
 *   - the **category** is a coarse bucket that `drawCues` turns into a *shape*
 *     on the cell. Six buckets rather than thirty-seven shapes, because a
 *     vocabulary of thirty-seven marks is its own puzzle; the shape says what
 *     kind of problem it is and the sentence says which one.
 *
 * Pure by construction: no canvas, no React, no config. `client/test/reasons.test.ts`
 * reads the shipped `packages/engine/src/interact.ts` and asserts every `fail('…')`
 * literal in it has an entry here, so a new engine refusal cannot ship anonymous.
 */

/** The six kinds of "no". The cue's shape is chosen from this, never from colour alone. */
export type CueCategory =
  /** You are missing a thing: a plate, a clean plate, a container, an ingredient. */
  | 'needs_something'
  /** Hands full, slot occupied, does not fit, wrong station for that item. */
  | 'hands_or_space'
  /** Burnt, dirty, rubbish — the thing exists and is spoiled. */
  | 'ruined'
  /** The station is alight and nothing else can happen until it is not. */
  | 'on_fire'
  /** Still cooking, still washing — right idea, wrong moment. */
  | 'not_yet'
  /** You both went for it. Not an ACTION_FAILED reason; the CONTESTED event. */
  | 'contested'

interface Refusal {
  phrase: string
  category: CueCategory
}

/** The reason string the feed uses for a CONTESTED cue, which the engine never emits as a failure. */
export const CONTESTED_REASON = 'contested'

/**
 * Every `fail('…')` literal in `packages/engine/src/interact.ts`, plus `contested`.
 *
 * Grouped the way the engine's own interaction table is grouped, so a new case
 * added there has an obvious home here.
 */
export const REASONS: Record<string, Refusal> = {
  // -- nothing at the other end -------------------------------------------
  nothing_there: { phrase: 'Nothing to use here.', category: 'needs_something' },
  nothing_to_put_down: { phrase: 'Your hands are empty.', category: 'needs_something' },

  // -- crates and the plate stack -----------------------------------------
  hands_full: { phrase: 'Your hands are full. Put something down.', category: 'hands_or_space' },
  crate_empty: { phrase: 'That crate is empty.', category: 'needs_something' },
  no_clean_plates: { phrase: 'No clean plates. Wash one at the sink.', category: 'needs_something' },
  stack_takes_plates: { phrase: 'Only clean, empty plates go on the stack.', category: 'hands_or_space' },
  plate_is_loaded: { phrase: 'Empty that plate in the bin first.', category: 'hands_or_space' },
  stack_is_full: { phrase: 'The plate stack is full.', category: 'hands_or_space' },

  // -- the counter ---------------------------------------------------------
  counter_empty: { phrase: 'That counter is empty.', category: 'needs_something' },
  counter_occupied: { phrase: 'That counter is full.', category: 'hands_or_space' },

  // -- the board -----------------------------------------------------------
  board_empty: { phrase: 'Put food on the board first.', category: 'needs_something' },
  board_takes_ingredients: { phrase: 'Only food goes on the board.', category: 'hands_or_space' },
  board_occupied: { phrase: 'That board is in use.', category: 'hands_or_space' },

  // -- the hob and the oven ------------------------------------------------
  no_container: { phrase: 'Put a pan on there first.', category: 'needs_something' },
  container_does_not_fit: { phrase: "That doesn't go there.", category: 'hands_or_space' },
  put_the_equipment_down: { phrase: 'Put the extinguisher back on its rack first.', category: 'hands_or_space' },
  equipment_occupied: { phrase: 'Something is already there.', category: 'hands_or_space' },
  not_cookable: { phrase: 'Chop it first.', category: 'needs_something' },
  // Already chopped, at the wrong heat: an oven does not stew, a stove does not roast.
  cooks_elsewhere: { phrase: 'This cooks somewhere else. Try the oven or stove.', category: 'hands_or_space' },
  // A pan cooks one thing at a time, so `pan_full` is now the commonest
  // refusal in the kitchen and the phrase has to teach the rule rather than
  // just report it.
  pan_full: { phrase: "One thing at a time. Plate what's in there first.", category: 'hands_or_space' },
  pan_empty: { phrase: "It's empty.", category: 'needs_something' },
  // Two presses share this reason now: dropping an ingredient into a burnt pan,
  // and trying to park a burnt pan back on the heat. Both have the same answer,
  // and it is a walk — so the phrase names the bin rather than the press.
  pan_burnt: { phrase: "It's burnt. Take it to the bin.", category: 'ruined' },
  contents_burnt: { phrase: 'Burnt! Bin it and start again.', category: 'ruined' },
  still_cooking: { phrase: 'Still cooking. Wait a moment.', category: 'not_yet' },
  needs_a_plate: { phrase: 'Grab a plate first.', category: 'needs_something' },
  plate_is_full: { phrase: 'That plate is full. Serve it or bin it.', category: 'hands_or_space' },

  // -- the sink ------------------------------------------------------------
  nothing_to_wash: { phrase: 'Nothing to wash.', category: 'needs_something' },
  sink_takes_plates: { phrase: 'Only plates go in the sink.', category: 'hands_or_space' },
  plate_already_clean: { phrase: 'That plate is already clean.', category: 'hands_or_space' },

  // -- the bin -------------------------------------------------------------
  // The bin deals with food and nothing else. Three of its four refusals are
  // therefore directions: the thing in your hands has a home, and this is not
  // it. Each phrase names the home, because "no" without a destination is the
  // refusal players report as the game being broken.
  nothing_to_bin: { phrase: 'Nothing to throw away.', category: 'needs_something' },
  container_is_empty: { phrase: "It's already empty.", category: 'needs_something' },
  rack_the_extinguisher: {
    phrase: 'The extinguisher goes back on its rack.',
    category: 'hands_or_space',
  },
  plates_go_home: {
    phrase: "Plates don't go in the bin. Clean ones go on the stack, dirty ones in the sink.",
    category: 'hands_or_space',
  },

  // -- the hatch -----------------------------------------------------------
  nothing_to_serve: { phrase: 'Bring a plated dish to serve.', category: 'needs_something' },
  plates_only: { phrase: 'Only plates can be served.', category: 'hands_or_space' },
  plate_is_dirty: { phrase: 'That plate is dirty. Wash it first.', category: 'ruined' },

  // -- the extinguisher rack ----------------------------------------------
  rack_empty: { phrase: 'The rack is empty. Someone has the extinguisher.', category: 'needs_something' },
  rack_takes_equipment: { phrase: 'Only the extinguisher goes here.', category: 'hands_or_space' },
  rack_occupied: { phrase: 'The extinguisher is already here.', category: 'hands_or_space' },

  // -- the plate return ----------------------------------------------------
  plate_return_takes_nothing: { phrase: 'Nothing goes on the plate return.', category: 'hands_or_space' },
  plate_return_empty: { phrase: 'No dirty plates yet.', category: 'needs_something' },

  // -- fire beats everything ----------------------------------------------
  station_on_fire: { phrase: "It's on fire! Grab the extinguisher and hold Space.", category: 'on_fire' },

  // -- not a failure, but the same channel --------------------------------
  contested: { phrase: 'You both reached for it. Only one of you can take it.', category: 'contested' },

  // -- refused before the engine ever saw it ------------------------------
  // These four are `InputRejectionReason`, not `fail('…')`: the transport threw
  // the press away, so there is no cell to mark and no tick it happened on.
  // They share this table because the player asked one question — "why did
  // nothing happen?" — and should not get two vocabularies for the answer.
  rate_limited: { phrase: 'Too many key presses. Slow down.', category: 'not_yet' },
  no_seat: { phrase: "You're not in the game yet. Finish setup first.", category: 'needs_something' },
  not_playing: { phrase: "The round hasn't started yet.", category: 'not_yet' },
  malformed: { phrase: "That key press didn't go through.", category: 'hands_or_space' },
  // Only ever sent to an agent seat, which moved sooner than its pace allows.
  agent_move_paced: { phrase: 'Too soon. AI chefs move at a set pace.', category: 'not_yet' },
}

/**
 * The reasons in `REASONS` that the engine never produces.
 *
 * Named so the completeness test can hold the engine's list and this one to the
 * same standard — everything in the table is either a `fail('…')` literal or is
 * listed here, and nothing is in the table by accident.
 */
export const NON_ENGINE_REASONS: ReadonlySet<string> = new Set([
  CONTESTED_REASON,
  'rate_limited',
  'no_seat',
  'not_playing',
  'malformed',
  'agent_move_paced',
])

/**
 * The phrase and category for a reason.
 *
 * Never throws and never returns undefined: an unrecognised reason — an old
 * replay, a server ahead of this bundle — reads as a generic refusal rather
 * than as a blank screen. The completeness test is what keeps that path empty
 * for the reasons we actually ship.
 */
export function refusalFor(reason: string | undefined): Refusal {
  if (reason === undefined) return UNKNOWN
  return REASONS[reason] ?? UNKNOWN
}

const UNKNOWN: Refusal = { phrase: "That won't work here.", category: 'hands_or_space' }
